import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from "@mui/material";
import { Delete, Edit, Add, Visibility } from "@mui/icons-material";
import { toast } from "react-toastify";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import CustomFormDialog from "../../Components/CustomFormDialog";
import { useNavigate } from "react-router";

const StockLocationList = () => {
    const authHeader = useAuthHeader();
    const [stockLocations, setStockLocations] = useState([]);
    const [isRefresh, setIsRefresh] = useState(false);

    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedLocationId, setSelectedLocationId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Fetch Stock Locations
    useEffect(() => {
        const fetchStockLocations = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(
                    `${REACT_APP_BACKEND_URL}/stock/stockLocation/get`,
                    {
                        headers: { Authorization: authHeader },
                    }
                );
                if (response.ok) {
                    const { stockLocations } = await response.json();
                    setStockLocations(stockLocations);
                } else {
                    toast.error("Failed to fetch stock locations");
                }
            } catch (error) {
                console.log(error);
                toast.error("Network error occurred");
            } finally {
                setIsLoading(false);
            }
        };

        fetchStockLocations();
    }, [authHeader, isRefresh]);

    // Handle Delete
    const deleteStockLocation = async () => {
        const response = await fetch(
            `${REACT_APP_BACKEND_URL}/stock/stockLocation/${selectedLocationId}`,
            {
                method: "DELETE",
                headers: { Authorization: authHeader },
            }
        );
        if (response.ok) {
            toast.success("Stock location deleted successfully");
            setIsRefresh(!isRefresh);
        } else {
            toast.error("Failed to delete stock location");
        }
        setOpenDeleteDialog(false);
    };

    // Open Edit Dialog
    const handleEdit = (location) => {
        setSelectedLocation(location);
        setIsEditDialogOpen(true);
    };

    const handleAddNew = () => {
        setIsAddDialogOpen(true);
    };

    const navigate = useNavigate();
    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Stock Locations
            </Typography>
            <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddNew}
            >
                Add New Location
            </Button>
            {!isLoading ? (
                <TableContainer sx={{ mt: 3 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {stockLocations.map((location) => (
                                <TableRow key={location.id}>
                                    <TableCell>{location.name}</TableCell>
                                    <TableCell>
                                        {location.description}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            color="primary"
                                            onClick={() => {
                                                navigate(
                                                    "/StockLocationItems/" +
                                                        location.id
                                                );
                                            }}
                                        >
                                            <Visibility />
                                        </IconButton>
                                        <IconButton
                                            color="primary"
                                            onClick={() => handleEdit(location)}
                                        >
                                            <Edit />
                                        </IconButton>
                                        <IconButton
                                            color="error"
                                            onClick={() => {
                                                setSelectedLocationId(
                                                    location.id
                                                );
                                                setOpenDeleteDialog(true);
                                            }}
                                        >
                                            <Delete />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Typography>Loading...</Typography>
            )}

            {/* Add Dialog */}
            <CustomFormDialog
                title="Add Stock Location"
                open={isAddDialogOpen}
                onClose={() => setIsAddDialogOpen(false)}
                fields={[
                    {
                        label: "Name",
                        name: "name",
                        type: "text",
                        placeholder: "Enter name",
                        required: true,
                        width: "49%",
                        validate: (value) =>
                            value.trim() === "" ? "Name is required" : "",
                    },
                    {
                        label: "Description",
                        name: "description",
                        type: "text",
                        required: true,
                        width: "49%",
                        multiline: true,
                    },
                ]}
                handleSubmitClick={async (formData) => {
                    const response = await fetch(
                        `${REACT_APP_BACKEND_URL}/stock/stockLocation/create`,
                        {
                            method: "POST",
                            headers: {
                                Authorization: authHeader,
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify(formData),
                        }
                    );
                    if (response.ok) {
                        toast.success("Stock location added successfully");
                        setIsRefresh(!isRefresh);
                    } else {
                        toast.error("Failed to add stock location");
                    }
                }}
            />

            {/* Edit Dialog */}
            {selectedLocation && (
                <CustomFormDialog
                    title="Edit Stock Location"
                    open={isEditDialogOpen}
                    onClose={() => setIsEditDialogOpen(false)}
                    fields={[
                        {
                            label: "Name",
                            name: "name",
                            type: "text",
                            placeholder: "Enter name",
                            required: true,
                            width: "49%",
                            value: selectedLocation.name,
                            defaultValue: selectedLocation.name,
                        },
                        {
                            label: "Description",
                            name: "description",
                            type: "text",
                            placeholder: "Enter name",
                            required: true,
                            width: "49%",
                            value: selectedLocation.description,
                            multiline: true,
                            defaultValue: selectedLocation.description,
                        },
                    ]}
                    handleSubmitClick={async (formData) => {
                        const response = await fetch(
                            `${REACT_APP_BACKEND_URL}/stock/stockLocation/${selectedLocation.id}`,
                            {
                                method: "PATCH",
                                headers: {
                                    Authorization: authHeader,
                                    "Content-Type": "application/json",
                                },
                                body: JSON.stringify(formData),
                            }
                        );
                        if (response.ok) {
                            toast.success(
                                "Stock location updated successfully"
                            );
                            setIsEditDialogOpen(false);
                            setIsRefresh(!isRefresh);
                        } else {
                            toast.error("Failed to update stock location");
                        }
                    }}
                />
            )}

            {/* Delete Dialog */}
            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this stock location?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={deleteStockLocation}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default StockLocationList;
